import { omit, has } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { VariantType } from 'notistack';
import {
  BankerMenuOptions,
  Bet,
  StakeErrors,
  Betslip,
  AuthorizationResponse,
  BettingSlipAcceptStatus,
  BetslipTicketType,
  BettingType,
  StakeErrorType,
  BetType,
} from '../@types';
import { BANKER_MENU_OPTIONS, WALLET_TYPE_OPTIONS, QUERY_KEYS } from '../constants';
import { ERROR_CODE } from '../constants/errorCodes';
import {
  resetBetslipBankers,
  resetBetslipSinglesStakes,
  setBetslipSelectedWallet,
  updateBetslipBetOdds,
  useBetslipCount,
  useBetslipCurrentBankerCount,
  useBetslipHasBetBuilderSelection,
  useBetslipIsWaysTicket,
  useBetslipSelectedWallet,
  useBetslipSelections,
  useBetslipTicketType,
} from '../stores/betslip/selectors';
import type { BetslipEvent, ErrorMessageOddsChangePayload } from '../types/betslip';
import { useBalances, useCountPendingBetslips, useGlobalTicketConditions } from '../queries';
import { useAuth } from '@neotech-solutions-org/neofusion-fe-shared';
import { WalletType } from '../types/enums';
import { SelectedUser } from '../components/molecules/UserSelect';
import {
  calculateMaxPayout,
  calculateTotalOddsForNormalBettingSlip,
  calculateMaxStakeAmountForNormalBettingSlip,
  getCombinations,
  calculateOddsRange,
} from 'sportsbook-shared-module';
import { BETSLIP_TICKET_TYPE, BETTING_TYPE_HIT_COUNT_MAP } from '../constants';
import { calculateRoundedMinStakeAmount, getErrorMessageType, mapStakeToCondition } from '../helpers';
import { isFeedApiError, postData } from '../utils/api';
import { useExtendedSnackbar } from './useExtendedSnackbar';
import { useInvalidateQuery } from './useInvalidateQuery';
import useLocalization from './useLocalization';
import { useRoles } from './useRoles';
import { TRIGGER_MAINTENANCE_MODE_EVENT } from '../router/MaintenanceProtectedRoute';
import { getBetBuilderSubmitItems, isBetBuilderBet } from '../utils/betBuilder';

type StakeAmounts = Partial<Record<BettingType, string>>;

export type StakeAmountInput = {
  [key in BettingType]: string;
};

export type BetslipCalculatedBet = BetslipEvent & {
  marketTypeId: string;
  marketTypeCombiningIds: string[];
  singlesStakeAmount: number;
};

type BetslipCalculations = {
  betTypes: BetType[];
  preparedBets: BetslipCalculatedBet[];
  maxPayout: number;
  totalStakeAmount: number;
  totalOdds: number;
  minWinningAmount: number | null;
  minOdds: number | null;
  maxOdds: number | null;
};

type UseBetslipStakeAmountsArgs = {
  removeSingleStakeError: (outcomeId: string) => void;
};

type UseBetslipStakeAmountsResult = {
  stakeAmounts: StakeAmountInput;
  setStakeAmounts: (amounts: StakeAmountInput) => void;
  updateStakeAmount: (key: string, value: string) => void;
  resetSystemStakeAmountValues: () => void;
};

type UseBetslipStakeErrorsResult = {
  stakeErrors: StakeErrors;
  setStakeErrors: React.Dispatch<React.SetStateAction<StakeErrors>>;
  removeSingleStakeError: (outcomeId: string) => void;
  clearStakeErrors: () => void;
};

type UseBetslipDisabledStateArgs = {
  totalStakeAmount: number;
  isAgent: boolean;
  selectedPlayerId?: string;
  hasPendingBetslips: boolean;
};

type UseBetslipPayloadArgs = {
  betTypes: { requiredHitCount: number; stakeAmountPerCombination: number }[];
  totalStakeAmount: number;
  isAgent: boolean;
  selectedPlayerId?: string;
};

type UsePlaceBetProps = {
  discardBetslip: () => void;
  onSuccess?: () => void;
};

type UsePendingBetslipTrackerProps = {
  onAllBetslipsResolved: () => void;
};

type CalculatedBet = Parameters<typeof calculateMaxPayout>[0]['bets'][number];

type PreparedBet = CalculatedBet & {
  marketTypeId: string;
  marketTypeCombiningIds?: string[];
  singlesStakeAmount: number;
};

type StakeValidationArgs = {
  totalOdds: number;
  preparedBets: PreparedBet[];
  disabledSystems: string[];
  setDisabledSystems: React.Dispatch<React.SetStateAction<string[]>>;
  stakeErrors: StakeErrors;
  setStakeErrors: React.Dispatch<React.SetStateAction<StakeErrors>>;
  stakeAmounts: Record<string, string>;
  updateStakeAmount: (key: string, value: string) => void;
  updateSinglesStakeAmount: (key: string, value: string) => void;
  combinations: Record<string, number>;
  removeSingleStakeError: (outcomeId: string) => void;
};

type StakeValidationResult = {
  validateStake: (
    key: string,
    value: string,
    isSingle?: boolean,
    betslipTicketType?: BetslipTicketType,
    numberOfCombinations?: number
  ) => void;
  handleStakeChange: (
    key: string,
    value: string,
    callback: (key: string, value: string) => void,
    isSingle?: boolean,
    betslipTicketType?: BetslipTicketType,
    numberOfCombinations?: number
  ) => void;
  updateStakes: React.MouseEventHandler<HTMLButtonElement>;
};

type BetCalculationData = BetslipEvent & {
  singlesStakeAmount: number;
  marketTypeId: string;
  marketTypeCombiningIds: string[];
};

type Combinations = Record<BettingType, number>;

export const useBetslipDrawerState = () => {
  const [open, setOpen] = useState(false);
  const [showSystemBetInputs, setShowSystemBetInputs] = useState(false);
  const [selectedMenuItem, setSelectedMenuItem] = useState<BankerMenuOptions>('normal');
  const [selectedPlayer, setSelectedPlayer] = useState<SelectedUser | null>(null);

  const closeBetslipDrawer = useCallback(() => {
    setOpen(false);
    setShowSystemBetInputs(false);
  }, []);

  const toggleSystemBetInputs = useCallback(() => {
    setShowSystemBetInputs((prev) => !prev);
  }, []);

  return {
    open,
    setOpen,
    showSystemBetInputs,
    setShowSystemBetInputs,
    toggleSystemBetInputs,
    selectedMenuItem,
    setSelectedMenuItem,
    selectedPlayer,
    setSelectedPlayer,
    closeBetslipDrawer,
  };
};

export const useBetslipMenuOptions = () => {
  const { getLocalizedConfig } = useLocalization();

  const localizedBankerMenuOptions = useMemo(() => getLocalizedConfig(BANKER_MENU_OPTIONS), [getLocalizedConfig]);
  const localizedWalletTypeOptions = useMemo(() => getLocalizedConfig(WALLET_TYPE_OPTIONS), [getLocalizedConfig]);

  return { localizedBankerMenuOptions, localizedWalletTypeOptions };
};

export const useBetslipBalance = () => {
  const selectedWallet = useBetslipSelectedWallet();
  const { mainBalance, bonusBalance } = useBalances();

  const balance = (selectedWallet === 'main' ? mainBalance?.balance : bonusBalance?.balance) || 0;

  return { balance, selectedWallet };
};

export const useBetslipStakeErrors = (): UseBetslipStakeErrorsResult => {
  const [stakeErrors, setStakeErrors] = useState<StakeErrors>({});

  const removeSingleStakeError = useCallback((outcomeId: string) => {
    setStakeErrors((prevState) => omit(prevState, outcomeId));
  }, []);

  const clearStakeErrors = useCallback(() => {
    setStakeErrors({});
  }, []);

  return { stakeErrors, setStakeErrors, removeSingleStakeError, clearStakeErrors };
};

export const useBetslipDisabledState = ({
  totalStakeAmount,
  isAgent,
  selectedPlayerId,
  hasPendingBetslips,
}: UseBetslipDisabledStateArgs) => {
  const { isAuthenticated } = useAuth();
  const bets = useBetslipSelections();
  const { balance } = useBetslipBalance();

  const hasEnoughBalance = balance >= totalStakeAmount;
  const hasDisabledBets = bets.some((bet) => bet.disabled);
  const baseDisabled = !totalStakeAmount || hasDisabledBets;
  const disabledByRole = isAgent ? !selectedPlayerId : !hasEnoughBalance;
  const showInsufficientFunds = !isAgent && isAuthenticated && !hasEnoughBalance && !hasPendingBetslips;

  return {
    hasEnoughBalance,
    hasDisabledBets,
    baseDisabled,
    disabledByRole,
    showInsufficientFunds,
    isAuthenticated,
  };
};

export const useBetslipCalculations = (stakeAmounts: StakeAmounts): BetslipCalculations => {
  const bets = useBetslipSelections();
  const currentBankerCount = useBetslipCurrentBankerCount();
  const betslipTicketType = useBetslipTicketType();
  const selectedWallet = useBetslipSelectedWallet();
  const { data: globalTicketConditions } = useGlobalTicketConditions();

  const betTypes = useMemo(
    () =>
      Object.entries(stakeAmounts)
        .map(([bettingType, stakeAmount]) => ({
          requiredHitCount: BETTING_TYPE_HIT_COUNT_MAP[bettingType as BettingType] - currentBankerCount,
          stakeAmountPerCombination: Number(stakeAmount),
        }))
        .filter((betType) => betType.stakeAmountPerCombination > 0 && betType.requiredHitCount > 0),
    [stakeAmounts, currentBankerCount]
  );

  const preparedBets = useMemo(
    () =>
      bets.map((bet) => ({
        ...bet,
        marketTypeId: bet.marketType.id,
        marketTypeCombiningIds: bet.marketType.marketTypeCombiningIds ?? [],
        singlesStakeAmount: bet.disabled ? 0 : Number(bet.singlesStakeAmount),
        specialValues: bet.specialValues,
      })),
    [bets]
  );

  const { maxTicketStake: globalMaxTicketStake, maxWinning: globalMaxWinning } =
    globalTicketConditions?.[selectedWallet]?.[betslipTicketType] ?? {};

  const { maxPayout, totalStakeAmount, minWinningAmount } = useMemo(
    () =>
      calculateMaxPayout(
        {
          bets: preparedBets,
          betTypes,
        },
        globalMaxWinning ?? Infinity,
        globalMaxTicketStake ?? Infinity
      ),
    [betTypes, globalMaxTicketStake, globalMaxWinning, preparedBets]
  );

  const totalOdds = useMemo(() => calculateTotalOddsForNormalBettingSlip(preparedBets), [preparedBets]);

  const oddsRangeHitCount = useMemo(() => {
    const entries = Object.keys(stakeAmounts);
    if (entries.length !== 1) return 0;

    const hitCount = BETTING_TYPE_HIT_COUNT_MAP[entries[0] as BettingType];
    return hitCount > 0 ? hitCount : 0;
  }, [stakeAmounts]);

  const { minOdds, maxOdds } = useMemo(
    () =>
      oddsRangeHitCount > 0 ? calculateOddsRange(preparedBets, oddsRangeHitCount) : { minOdds: null, maxOdds: null },
    [preparedBets, oddsRangeHitCount]
  );

  return {
    betTypes,
    preparedBets,
    maxPayout,
    totalStakeAmount,
    totalOdds,
    minWinningAmount,
    minOdds,
    maxOdds,
  };
};

export const useBetslipStakeAmounts = ({
  removeSingleStakeError,
}: UseBetslipStakeAmountsArgs): UseBetslipStakeAmountsResult => {
  const [stakeAmounts, setStakeAmounts] = useState<StakeAmountInput>({} as StakeAmountInput);

  const updateStakeAmount = useCallback(
    (key: string, value: string) => {
      setStakeAmounts((prevState) => ({
        ...prevState,
        [key]: value,
      }));
    },
    [setStakeAmounts]
  );

  const resetSystemStakeAmountValues = useCallback(() => {
    setStakeAmounts((prevState) => {
      const updatedStakeAmounts = Object.keys(prevState).reduce((acc, key) => {
        removeSingleStakeError(key);
        return { ...acc, [key]: '' };
      }, {} as StakeAmountInput);
      return updatedStakeAmounts;
    });
  }, [setStakeAmounts, removeSingleStakeError]);

  return {
    stakeAmounts,
    setStakeAmounts,
    updateStakeAmount,
    resetSystemStakeAmountValues,
  };
};

export const useBetslipCombinations = (
  bets: BetCalculationData[],
  currentBankerCount: number,
  showSystemBetInputs: boolean,
  drawerOpen: boolean
) => {
  const [combinations, setCombinations] = useState<Partial<Combinations>>({});
  const allCombinationsCalculatedRef = useRef(false);
  useEffect(() => {
    allCombinationsCalculatedRef.current = false;
  }, [bets.length, currentBankerCount]);

  useEffect(() => {
    if (!drawerOpen || allCombinationsCalculatedRef.current) return;

    const getOnlyLastCombination = !showSystemBetInputs;

    const result = getCombinations(bets, getOnlyLastCombination);
    setCombinations(result);

    if (showSystemBetInputs) {
      allCombinationsCalculatedRef.current = true;
    }
  }, [bets, currentBankerCount, showSystemBetInputs, drawerOpen]);

  return combinations;
};

export const useBetslipStakeValidation = ({
  totalOdds,
  preparedBets,
  disabledSystems,
  setDisabledSystems,
  stakeErrors,
  setStakeErrors,
  stakeAmounts,
  updateStakeAmount,
  updateSinglesStakeAmount,
  combinations,
  removeSingleStakeError,
}: StakeValidationArgs): StakeValidationResult => {
  const bets = useBetslipSelections();
  const betslipTicketType = useBetslipTicketType();
  const selectedWallet = useBetslipSelectedWallet();
  const currentBankerCount = useBetslipCurrentBankerCount();
  const { data: globalTicketConditions } = useGlobalTicketConditions();

  const globalMaxWinning = globalTicketConditions?.[selectedWallet]?.[betslipTicketType]?.maxWinning;

  const getMaxWinningStake = useCallback(
    (outcomeId: string, isMultipleTicket = false) => {
      const singleBet = bets.find((bet) => bet.outcomeId === outcomeId);
      let ticketType = betslipTicketType;
      let ticketOdds = totalOdds;
      if (singleBet) {
        const { odds, isLive } = singleBet;
        ticketOdds = Number(odds);
        ticketType = isLive ? BETSLIP_TICKET_TYPE.IN_PLAY : BETSLIP_TICKET_TYPE.PRE_MATCH;
      }
      const singleMaxWin = globalTicketConditions?.[selectedWallet]?.[ticketType]?.maxWinning ?? Infinity;
      return isMultipleTicket
        ? Infinity
        : calculateMaxStakeAmountForNormalBettingSlip(Number(ticketOdds), singleMaxWin);
    },
    [bets, betslipTicketType, globalTicketConditions, selectedWallet, totalOdds]
  );

  const updateStakeErrors = useCallback(
    (key: string, value: StakeErrorType) => {
      setStakeErrors((prevState) => {
        return { ...prevState, [key]: { errorType: value } };
      });
    },
    [setStakeErrors]
  );

  const updateStakes = useCallback<React.MouseEventHandler<HTMLButtonElement>>(
    (event) => {
      event.preventDefault();
      Object.entries(stakeErrors).forEach(([key, { errorType }]) => {
        const singleTicketBet = bets.find((bet) => bet.outcomeId === key);
        const singleTicketType = singleTicketBet?.isLive ? BETSLIP_TICKET_TYPE.IN_PLAY : BETSLIP_TICKET_TYPE.PRE_MATCH;
        const ticketType = singleTicketBet ? singleTicketType : betslipTicketType;

        const combinationCount = Object.entries(combinations).find(([combinationKey]) => combinationKey === key)?.[1];
        const updatedStake = mapStakeToCondition(
          ticketType,
          errorType,
          selectedWallet,
          globalTicketConditions,
          combinationCount
        );

        if (!updatedStake) return;

        removeSingleStakeError(key);
        if (has(stakeAmounts, key)) {
          updateStakeAmount(key, updatedStake.toFixed(2).toString());
          return;
        }

        updateSinglesStakeAmount(key, updatedStake.toFixed(2).toString());
      });
    },
    [
      bets,
      betslipTicketType,
      combinations,
      globalTicketConditions,
      removeSingleStakeError,
      stakeAmounts,
      stakeErrors,
      updateSinglesStakeAmount,
      updateStakeAmount,
      selectedWallet,
    ]
  );

  const validateStake = useCallback(
    (key: string, value: string, isSingle = false, betslipTicketType?: BetslipTicketType, numberOfCombinations = 1) => {
      const isValidValue = (value: string) => {
        return value !== '' && Number(value) !== 0;
      };

      if (!globalTicketConditions || !betslipTicketType) {
        return;
      }

      const stakeAmount = Number(value);
      const activeConditions = globalTicketConditions[selectedWallet][betslipTicketType];

      const { minTicketStake: minStake, maxTicketStake, maxStakeSingle } = activeConditions;
      const filteredBets = preparedBets.map((bet) => ({
        ...bet,
        singlesStakeAmount: 0,
      })) as CalculatedBet[];

      const betTypes = [
        {
          requiredHitCount: BETTING_TYPE_HIT_COUNT_MAP[key as BettingType] - currentBankerCount,
          stakeAmountPerCombination: Number(stakeAmount),
        },
      ].filter((betType) => betType.stakeAmountPerCombination > 0 && betType.requiredHitCount > 0);

      const { maxPayout } = calculateMaxPayout(
        {
          bets: filteredBets,
          betTypes,
        },
        Infinity,
        Infinity
      );

      const hasSingleMaxStake = isSingle && !!maxStakeSingle;

      const maxStake = (hasSingleMaxStake ? maxStakeSingle : maxTicketStake) ?? Infinity;

      const maxStakePerCombination =
        numberOfCombinations > 1
          ? calculateMaxStakeAmountForNormalBettingSlip(numberOfCombinations, maxStake)
          : maxStake;

      const maxWinningStake = getMaxWinningStake(key, numberOfCombinations > 1);

      const minStakePerCombination =
        numberOfCombinations > 1 ? calculateRoundedMinStakeAmount(numberOfCombinations, minStake ?? 0) : minStake;

      if (disabledSystems.includes(key)) {
        setDisabledSystems((prev) => prev.filter((system) => system !== key));
      }

      if (!isValidValue(value)) {
        setStakeErrors((prevErrors) => {
          const updatedErrors = { ...prevErrors };
          delete updatedErrors[key];
          return updatedErrors;
        });
      } else {
        if (minStakePerCombination && stakeAmount < minStakePerCombination) {
          updateStakeErrors(key, getErrorMessageType(isSingle, betslipTicketType, 'min'));
        } else if (stakeAmount > Math.min(maxWinningStake, maxStakePerCombination)) {
          updateStakeErrors(
            key,
            maxWinningStake < maxStakePerCombination
              ? 'maxWinLimit'
              : getErrorMessageType(hasSingleMaxStake, betslipTicketType, 'max')
          );
        } else if (globalMaxWinning && maxPayout > globalMaxWinning) {
          setDisabledSystems((prev) => [...prev, key]);
          updateStakeErrors(key, 'maxWinLimit');
        } else {
          setStakeErrors((prevErrors) => {
            const updatedErrors = { ...prevErrors };
            delete updatedErrors[key];
            return updatedErrors;
          });
        }
      }
    },
    [
      currentBankerCount,
      disabledSystems,
      getMaxWinningStake,
      globalMaxWinning,
      globalTicketConditions,
      preparedBets,
      selectedWallet,
      setDisabledSystems,
      setStakeErrors,
      updateStakeErrors,
    ]
  );

  const handleStakeChange = useCallback(
    (
      key: string,
      value: string,
      callback: (key: string, value: string) => void,
      isSingle = false,
      betslipTicketType?: BetslipTicketType,
      numberOfCombinations = 1
    ) => {
      validateStake(key, value, isSingle, betslipTicketType, numberOfCombinations);
      callback(key, value);
    },
    [validateStake]
  );

  return {
    validateStake,
    handleStakeChange,
    updateStakes,
  };
};

export const useBetslipPayload = ({ betTypes, totalStakeAmount, isAgent, selectedPlayerId }: UseBetslipPayloadArgs) => {
  const bets = useBetslipSelections();
  const selectedWallet = useBetslipSelectedWallet();

  const preparePayload = useCallback(() => {
    const betsPayload: Bet[] = bets
      .filter((bet) => !bet.disabled && !isBetBuilderBet(bet))
      .map((bet) => ({
        eventId: bet.eventId,
        outcomeId: bet.outcomeId,
        marketId: bet.marketId,
        odds: bet.odds,
        singlesStakeAmount: Number(bet.singlesStakeAmount || 0) || undefined,
        banker: bet.banker,
      }));

    return {
      bets: betsPayload,
      betBuilder: getBetBuilderSubmitItems(bets.filter((bet) => !bet.disabled)),
      betTypes,
      walletType: selectedWallet,
      totalStakeAmount,
      ...(isAgent && selectedPlayerId && { userId: selectedPlayerId }),
    };
  }, [bets, betTypes, selectedWallet, totalStakeAmount, isAgent, selectedPlayerId]);

  return { preparePayload };
};

export const useBetslipSystemTicket = (betTypes: { requiredHitCount: number }[]) => {
  const betCount = useBetslipCount();

  const isSystemTicket = useMemo(() => {
    if (betTypes.length > 1) return true;
    if (betTypes.length === 1) return betTypes[0].requiredHitCount !== betCount;
    return false;
  }, [betTypes, betCount]);

  return { isSystemTicket };
};

const getClientMessage = (detailedResults: AuthorizationResponse['detailedResults']) =>
  Array.from(new Set(detailedResults.map((result) => result.clientMessage).filter(Boolean))).join('\n');

const processAndDisplayMessages = (
  enqueueWithDelay: ReturnType<typeof useExtendedSnackbar>['enqueueWithDelay'],
  messages: string[],
  baseDelay: number,
  messageDelay: number,
  variant: VariantType
) => {
  messages.forEach((message, index) => {
    enqueueWithDelay(
      message,
      { variant, style: { whiteSpace: 'pre-line' }, preventDuplicate: false },
      baseDelay + messageDelay * index
    );
  });
};

export const usePlaceBet = ({ discardBetslip, onSuccess }: UsePlaceBetProps) => {
  const { closeSnackbar, showTicketAuthorizationCountMessage, showErrorMessage, enqueueWithDelay, enqueueSnackbar } =
    useExtendedSnackbar();
  const invalidateData = useInvalidateQuery();
  const { t } = useLocalization();
  const { isAgent } = useRoles();

  const placeBetMutation = useMutation({
    mutationFn: (newBetslip: Betslip) => {
      const endpoint = isAgent ? 'bo/betting-slips' : 'betting-slips';
      return postData(endpoint, newBetslip);
    },
    onMutate: () => {
      enqueueSnackbar(t('awaitingConfirmation'), {
        variant: 'info',
        persist: true,
        key: 'awaitingConfirmation',
        transitionDuration: 0,
      });
    },
    onSuccess: (data: AuthorizationResponse[]) => {
      closeSnackbar('awaitingConfirmation');
      if (Array.isArray(data)) {
        const MESSAGE_DELAY = 3000;

        const rejectedMessages: string[] = [];
        const pendingMessages: string[] = [];

        const statusCounts = data.reduce(
          (counts, { acceptStatus, detailedResults }) => {
            counts[acceptStatus] = (counts[acceptStatus] || 0) + 1;

            const message = getClientMessage(detailedResults);

            if (acceptStatus === 'rejected') {
              rejectedMessages.push(message || t('bettingSlipRejected'));
            }

            if (acceptStatus === 'pending') {
              pendingMessages.push(message || t('bettingSlipPending'));
            }

            return counts;
          },
          { pending: 0, rejected: 0, accepted: 0 } as Record<Partial<BettingSlipAcceptStatus>, number>
        );

        let cumulativeDelay = 0;

        if (statusCounts.pending > 0) {
          processAndDisplayMessages(enqueueWithDelay, pendingMessages, cumulativeDelay, MESSAGE_DELAY, 'warning');
          cumulativeDelay += statusCounts.pending * MESSAGE_DELAY;
        }

        if (statusCounts.rejected > 0) {
          processAndDisplayMessages(enqueueWithDelay, rejectedMessages, cumulativeDelay, MESSAGE_DELAY, 'error');
          cumulativeDelay += statusCounts.rejected * MESSAGE_DELAY;
        }

        if (statusCounts.accepted > 0) {
          showTicketAuthorizationCountMessage('accepted', statusCounts.accepted, cumulativeDelay);
        }

        if (!statusCounts.pending) {
          discardBetslip();
        }

        invalidateData([
          QUERY_KEYS.myBetsCount,
          QUERY_KEYS.myBets,
          QUERY_KEYS.balance,
          QUERY_KEYS.jackpots,
          QUERY_KEYS.pendingBetslipsCount,
        ]);

        onSuccess?.();
      }
    },

    onError: (error) => {
      closeSnackbar('awaitingConfirmation');

      if (!(error instanceof AxiosError)) {
        enqueueSnackbar(t('unexpectedError'), { variant: 'error' });
        return;
      }

      const response = error.response;
      const httpCode = response?.status;

      if (httpCode === 503) {
        window.dispatchEvent(new CustomEvent(TRIGGER_MAINTENANCE_MODE_EVENT));
        return;
      }

      const errorMessageObject = response?.data?.errorMessage;

      if (isFeedApiError<ErrorMessageOddsChangePayload>(errorMessageObject)) {
        if (errorMessageObject.code === ERROR_CODE.ODDS_CHANGED) {
          const updatedOutcome = errorMessageObject.additionalData;
          if (updatedOutcome) {
            updateBetslipBetOdds(updatedOutcome);
          }
        } else {
          showErrorMessage(errorMessageObject.code, errorMessageObject.params);
        }
        return;
      }

      enqueueSnackbar(t('unexpectedError'), { variant: 'error' });
    },
  });

  return { placeBetMutation };
};

export const usePendingBetslipTracker = ({ onAllBetslipsResolved }: UsePendingBetslipTrackerProps) => {
  const { data: pendingBetslipsCount, isSuccess: pendingBetslipsCountSuccess } = useCountPendingBetslips();
  const [previousPendingCount, setPreviousPendingCount] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (!pendingBetslipsCountSuccess) {
      return;
    }

    const currentPendingCount = pendingBetslipsCount?.count;
    const hasPreviousCount = typeof previousPendingCount === 'number';

    const hasResolvedAllPendingBetslips = currentPendingCount === 0 && hasPreviousCount && previousPendingCount > 0;

    if (hasResolvedAllPendingBetslips) {
      onAllBetslipsResolved();
    }

    setPreviousPendingCount(currentPendingCount);
  }, [pendingBetslipsCountSuccess, pendingBetslipsCount?.count, onAllBetslipsResolved, previousPendingCount]);

  return {
    hasPendingBetslips: !!pendingBetslipsCount?.count,
  };
};

export const useBetslipMenuHandlers = (
  setSelectedMenuItem: React.Dispatch<React.SetStateAction<BankerMenuOptions>>,
  setStakeAmounts: (amounts: StakeAmountInput) => void
) => {
  const handleMenuChange = useCallback(
    (value: string) => {
      resetBetslipSinglesStakes();
      setStakeAmounts({} as StakeAmountInput);
      resetBetslipBankers();
      setSelectedMenuItem(value as BankerMenuOptions);
    },
    [setStakeAmounts, setSelectedMenuItem]
  );

  const handleWalletChange = useCallback((value: string) => {
    setBetslipSelectedWallet(value as WalletType);
  }, []);

  return { handleMenuChange, handleWalletChange };
};

export const useBetslipEffects = (
  selectedMenuItem: BankerMenuOptions,
  setSelectedMenuItem: React.Dispatch<React.SetStateAction<BankerMenuOptions>>,
  closeBetslipDrawer: () => void,
  resetSystemStakeAmountValues: () => void,
  clearStakeErrors: () => void
) => {
  const betCount = useBetslipCount();
  const isWaysTicket = useBetslipIsWaysTicket();
  const hasBetBuilderSelection = useBetslipHasBetBuilderSelection();

  useEffect(() => {
    resetSystemStakeAmountValues();
  }, [betCount, resetSystemStakeAmountValues]);

  useEffect(() => {
    if (betCount === 0) {
      clearStakeErrors();
      closeBetslipDrawer();
    }
  }, [betCount, closeBetslipDrawer, clearStakeErrors]);

  useEffect(() => {
    if (betCount < 3 && selectedMenuItem === 'bankers') {
      setSelectedMenuItem('normal');
    }
  }, [betCount, selectedMenuItem, setSelectedMenuItem]);

  useEffect(() => {
    if (isWaysTicket) {
      resetBetslipBankers();
      setSelectedMenuItem('normal');
    }
  }, [isWaysTicket, setSelectedMenuItem]);

  useEffect(() => {
    if (hasBetBuilderSelection) {
      setSelectedMenuItem('normal');
      resetSystemStakeAmountValues();
    }
  }, [hasBetBuilderSelection, resetSystemStakeAmountValues, setSelectedMenuItem]);
};
